import {
	Button,
	Modal,
	ModalBody,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	useToast,
	Heading,
	HStack,
} from '@tempo/core';
import {useTranslation} from 'next-i18next';
import Toast, {INTENT} from '@components/Toast';
import {useGroupLeaveMutation} from '@graphql/generated';
import {useRouter} from 'next/router';

export type GroupSelected = {
	id: string;
	name: string;
	hasManyMembers: boolean;
};

interface LeaveGroupModalProps {
	isOpen: boolean;
	onClose: () => void;
	groupSelected: GroupSelected;
}

const LeaveGroupModal = ({
	isOpen,
	onClose,
	groupSelected,
}: LeaveGroupModalProps) => {
	const {t} = useTranslation();
	const router = useRouter();
	const toast = useToast({
		duration: 3000,
	});

	const [leaveGroup] = useGroupLeaveMutation({
		variables: {
			input: {
				musicTogetherGroupId: groupSelected.id,
			},
		},
		update: (cache) => {
			cache.modify({
				id: cache.identify({
					__typename: 'MusicTogetherGroup',
					id: groupSelected.id,
				}),
				fields: (_, {DELETE}) => DELETE,
			});
		},
		onError: () => {
			onClose();

			toast({
				render: () => (
					<Toast intent={INTENT.ERROR}>
						{t('errormessage_text_anerroroccurredpleasetryagain_web')}
					</Toast>
				),
			});
		},
		onCompleted: ({musicTogetherLeaveGroup}) => {
			onClose();

			if (
				musicTogetherLeaveGroup.__typename === 'MusicTogetherLeaveGroupError'
			) {
				return toast({
					render: () => (
						<Toast intent={INTENT.ERROR}>
							{t('errormessage_text_anerroroccurredpleasetryagain_web')}
						</Toast>
					),
				});
			}

			if (
				musicTogetherLeaveGroup.__typename === 'MusicTogetherLeaveGroupOutput'
			) {
				toast.closeAll();

				toast({
					render: () => (
						<Toast intent={INTENT.SUCCESS}>
							{t('musictogethertoastmessage_text_youleftXgroup_web', {
								sprintf: [groupSelected.name],
							})}
						</Toast>
					),
				});

				if (router.pathname.includes('/group/[groupId]')) {
					router.push('/groups');
				}
			}
		},
	});

	return (
		<Modal onClose={onClose} isOpen={isOpen} isCentered>
			<ModalOverlay />
			<ModalContent mx="spacing.l" textAlign={['left', 'center']}>
				<ModalHeader>
					<Heading variant="heading.m" color="text.neutral.primary.default">
						{t('musictogetherinappbox_title_wanttoleaveXgroup_web', {
							sprintf: [groupSelected.name],
						})}
					</Heading>
				</ModalHeader>
				<ModalBody>
					<Text variant="body.s.default" color="text.neutral.secondary.default">
						{groupSelected.hasManyMembers
							? t('musictogetherinappbox_text_nolongercontributetomix_web')
							: t('musictogetherinappbox_text_lastmembergroupdeleted_web')}
					</Text>
				</ModalBody>
				<ModalFooter>
					<HStack spacing="spacing.s" justify={['left', 'center']}>
						<Button
							bg="background.feedback.error.default"
							_hover={{
								bg: 'background.feedback.error.hovered',
							}}
							_active={{
								bg: 'background.feedback.error.pressed',
							}}
							onClick={() => leaveGroup()}
						>
							{t('generic_action_yes_web')}
						</Button>

						<Button onClick={onClose} variant="outline">
							{t('generic_action_no_web')}
						</Button>
					</HStack>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default LeaveGroupModal;
